import React, { useRef, useState, useEffect, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import {
  Play, Pause, Stop, FloppyDisk, Plus, Trash, MusicNotes,
  UploadSimple, SpeakerHigh, SpeakerSlash, Waveform,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// ── Synth note table ─────────────────────────────────────────────────────────
const NOTES = ["C", "D", "E", "F", "G", "A", "B"];
const NOTE_FREQ = { C:261.63, D:293.66, E:329.63, F:349.23, G:392.00, A:440.00, B:493.88 };
const SYNTH_TRACKS = [
  { id:"lead",  label:"LEAD",  type:"square",   octave:1,  color:"#22d3ee" },
  { id:"bass",  label:"BASS",  type:"triangle", octave:-1, color:"#84cc16" },
  { id:"arp",   label:"ARP",   type:"sawtooth", octave:0,  color:"#a78bfa" },
  { id:"drum",  label:"DRUM",  type:"noise",    octave:0,  color:"#f97316" },
];
const STEPS = 16;

function emptyPattern() {
  const p = {};
  SYNTH_TRACKS.forEach(tr => {
    p[tr.id] = Array(STEPS).fill(null).map(() => ({ on:false, note:"C" }));
  });
  return p;
}

function freqFor(note, octaveShift) {
  return (NOTE_FREQ[note] || NOTE_FREQ.C) * Math.pow(2, octaveShift);
}

// ── Waveform visualizer ──────────────────────────────────────────────────────
function WaveformBar({ analyser }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    if (!analyser || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const data   = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(data);
      ctx.fillStyle = "#09090b";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#22d3ee";
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      const slice = canvas.width / data.length;
      let x = 0;
      data.forEach((v, i) => {
        const y = (v / 128) * (canvas.height / 2);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        x += slice;
      });
      ctx.stroke();
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [analyser]);

  return (
    <canvas
      ref={canvasRef}
      width={320} height={40}
      className="border border-zinc-800 bg-[#09090b]"
      style={{ width: "100%", maxWidth: 320, height: 40 }}
    />
  );
}

// ── Imported sample clip card ─────────────────────────────────────────────────
function SampleClip({ clip, onDelete, onPlay }) {
  const [playing, setPlaying] = useState(false);
  const srcRef = useRef(null);

  const play = () => {
    if (playing) { srcRef.current?.stop(); setPlaying(false); return; }
    onPlay(clip.buffer, (src) => {
      srcRef.current = src;
      setPlaying(true);
      src.onended = () => setPlaying(false);
    });
  };

  return (
    <div className="flex items-center gap-2 border border-zinc-800 px-3 py-2 bg-zinc-900">
      <button onClick={play} className="text-cyan-400 hover:text-cyan-300">
        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-zinc-200 truncate">{clip.name}</div>
        <div className="text-[10px] text-zinc-600">{clip.duration.toFixed(2)}s · {clip.sizeKB}KB</div>
      </div>
      <div
        className="text-[9px] font-mono px-2 py-0.5 border"
        style={{ borderColor: clip.color + "88", color: clip.color }}
      >
        {clip.category}
      </div>
      <button onClick={() => onDelete(clip.id)} className="text-zinc-600 hover:text-red-400 ml-1">
        <Trash size={12} />
      </button>
      {clip.data_url && (
        <a href={clip.data_url} download={clip.name} className="text-[10px] text-cyan-400 ml-1">↓</a>
      )}
    </div>
  );
}

// ── Main AudioPanel ───────────────────────────────────────────────────────────
export default function AudioPanel({ project, onChange }) {
  const { t } = useI18n();

  // Audio context (lazy)
  const ctxRef      = useRef(null);
  const analyserRef = useRef(null);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      analyserRef.current = ctxRef.current.createAnalyser();
      analyserRef.current.fftSize = 1024;
      analyserRef.current.connect(ctxRef.current.destination);
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  // Sequencer state
  const [bpm,        setBpm]        = useState(120);
  const [pattern,    setPattern]    = useState(emptyPattern());
  const [activeStep, setActiveStep] = useState(-1);
  const [playing,    setPlaying]    = useState(false);
  const [name,       setName]       = useState("Soundtrack 1");

  // Per-track state
  const [volumes,  setVolumes]  = useState(() => Object.fromEntries(SYNTH_TRACKS.map(t => [t.id, 0.8])));
  const [muted,    setMuted]    = useState(() => Object.fromEntries(SYNTH_TRACKS.map(t => [t.id, false])));

  // Sample library
  const [samples, setSamples] = useState(project.samples || []);
  const [sampleCat, setSampleCat] = useState("ALL");
  const buffersRef = useRef({});  // id → AudioBuffer

  // Refs for sequencer loop
  const playingRef = useRef(false);
  const stepRef    = useRef(0);
  const timerRef   = useRef(null);
  const patternRef = useRef(pattern);
  const bpmRef     = useRef(bpm);
  const volumesRef = useRef(volumes);
  const mutedRef   = useRef(muted);

  useEffect(() => { patternRef.current = pattern; }, [pattern]);
  useEffect(() => { bpmRef.current     = bpm;     }, [bpm]);
  useEffect(() => { volumesRef.current = volumes; }, [volumes]);
  useEffect(() => { mutedRef.current   = muted;   }, [muted]);

  // ── playStep ───────────────────────────────────────────────
  const playStep = useCallback((i) => {
    const ctx = ensureCtx();
    const now = ctx.currentTime;
    const stepDur = 60 / bpmRef.current / 4;
    const dest = analyserRef.current || ctx.destination;

    SYNTH_TRACKS.forEach(tr => {
      if (mutedRef.current[tr.id]) return;
      const cell = patternRef.current[tr.id][i];
      if (!cell?.on) return;
      const vol = volumesRef.current[tr.id] ?? 0.8;

      const gainNode = ctx.createGain();
      gainNode.gain.value = 0;
      gainNode.connect(dest);

      if (tr.type === "noise") {
        const bufSize = Math.floor(0.05 * ctx.sampleRate);
        const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let s = 0; s < bufSize; s++) data[s] = (Math.random()*2-1) * (1-s/bufSize);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(gainNode);
        gainNode.gain.setValueAtTime(vol * 0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        src.start(now); src.stop(now + 0.07);
      } else {
        const osc = ctx.createOscillator();
        osc.type = tr.type;
        osc.frequency.value = freqFor(cell.note, tr.octave);
        osc.connect(gainNode);
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(vol * 0.2, now + 0.005);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + stepDur * 0.88);
        osc.start(now); osc.stop(now + stepDur);
      }
    });
  }, [ensureCtx]);

  const start = () => {
    if (playingRef.current) return;
    playingRef.current = true; setPlaying(true); stepRef.current = 0;
    const tick = () => {
      if (!playingRef.current) return;
      setActiveStep(stepRef.current);
      playStep(stepRef.current);
      stepRef.current = (stepRef.current + 1) % STEPS;
      timerRef.current = setTimeout(tick, (60 / bpmRef.current / 4) * 1000);
    };
    tick();
  };
  const stop = () => {
    playingRef.current = false; setPlaying(false); setActiveStep(-1);
    clearTimeout(timerRef.current);
  };
  useEffect(() => () => stop(), []);

  // ── Sample import ──────────────────────────────────────────
  const CATEGORY_COLORS = { SFX:"#22d3ee", BGM:"#a78bfa", AMBIENCE:"#84cc16", CUSTOM:"#f97316" };

  const handleImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ctx = ensureCtx();
    for (const file of files) {
      try {
        const arrayBuf = await file.arrayBuffer();
        const audioBuf = await ctx.decodeAudioData(arrayBuf);
        const id  = crypto.randomUUID();
        // Determine category from filename heuristic
        const lower = file.name.toLowerCase();
        const cat = lower.includes("bgm") || lower.includes("music") ? "BGM"
          : lower.includes("sfx") || lower.includes("hit") || lower.includes("jump") ? "SFX"
          : lower.includes("amb") ? "AMBIENCE" : "CUSTOM";
        const clip = {
          id, name: file.name, duration: audioBuf.duration,
          sizeKB: Math.round(file.size / 1024),
          category: cat, color: CATEGORY_COLORS[cat],
          data_url: await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); }),
        };
        buffersRef.current[id] = audioBuf;
        setSamples(s => {
          const next = [...s, clip];
          onChange({ samples: next });
          return next;
        });
        toast.success(`Importado: ${file.name}`);
      } catch {
        toast.error(`Error al decodificar: ${file.name}`);
      }
    }
    e.target.value = "";
  };

  const handlePlaySample = (buffer, cb) => {
    const ctx = ensureCtx();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(analyserRef.current || ctx.destination);
    src.start();
    cb(src);
  };

  const handleDeleteSample = (id) => {
    delete buffersRef.current[id];
    setSamples(s => {
      const next = s.filter(x => x.id !== id);
      onChange({ samples: next });
      return next;
    });
  };

  // ── Save ───────────────────────────────────────────────────
  const handleSave = () => {
    const soundtracks = project.soundtracks || [];
    const track = {
      id: crypto.randomUUID(), name: name || "Soundtrack",
      bpm, pattern, volumes, muted,
      created_at: new Date().toISOString(),
    };
    onChange({ soundtracks: [...soundtracks, track], samples });
    toast.success(t("ide.saved"));
  };

  const handleLoad = (s) => {
    setName(s.name); setBpm(s.bpm); setPattern(s.pattern);
    if (s.volumes) setVolumes(s.volumes);
    if (s.muted)   setMuted(s.muted);
  };

  const handleDelete = (id) => {
    onChange({ soundtracks: (project.soundtracks||[]).filter(s => s.id !== id) });
  };

  // Export code snippet
  const handleExportCode = () => {
    const lines = ["// GameForge Audio — Phaser 3 snippet", "// paste in your scene create():", ""];
    samples.forEach(c => {
      lines.push(`// this.load.audio('${c.name.replace(/\.[^.]+$/,"")}', 'assets/audio/${c.name}');`);
    });
    lines.push("");
    lines.push("// In game logic:");
    samples.forEach(c => {
      const key = c.name.replace(/\.[^.]+$/,"").replace(/[^a-zA-Z0-9_]/g,"_");
      lines.push(`// this.sound.play('${key}');`);
    });
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Snippet de audio copiado");
  };

  const toggleMute = (id) => setMuted(m => ({ ...m, [id]: !m[id] }));
  const setVolume  = (id, v) => setVolumes(vs => ({ ...vs, [id]: parseFloat(v) }));
  const toggleCell = (trId, idx) =>
    setPattern(p => ({ ...p, [trId]: p[trId].map((c,i) => i===idx ? {...c, on:!c.on} : c) }));
  const setNote = (trId, idx, note) =>
    setPattern(p => ({ ...p, [trId]: p[trId].map((c,i) => i===idx ? {...c, note} : c) }));
  const clearAll = () => setPattern(emptyPattern());

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 flex items-center gap-2">
          <MusicNotes size={14} weight="duotone" /> SOUNDTRACK STUDIO
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* BPM */}
          <div className="flex items-center gap-1 border border-zinc-800 bg-zinc-900 px-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">BPM</span>
            <input
              type="number" min={40} max={240} value={bpm}
              onChange={e => setBpm(Math.max(40, Math.min(240, parseInt(e.target.value)||120)))}
              className="bg-transparent w-14 text-sm font-mono text-zinc-100 outline-none px-2 py-1"
            />
          </div>
          {!playing
            ? <Button onClick={start} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2 h-8 text-xs"><Play size={12} weight="fill"/>Play</Button>
            : <Button onClick={stop}  className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 rounded-none font-semibold gap-2 h-8 text-xs"><Stop size={12} weight="fill"/>Stop</Button>
          }
          <Button onClick={clearAll} variant="ghost" className="rounded-none text-red-400 border border-zinc-800 hover:bg-zinc-900 gap-2 h-8 text-xs">
            <Trash size={12}/> Clear
          </Button>
          <Button onClick={handleExportCode} variant="ghost" className="rounded-none text-cyan-300 border border-zinc-800 hover:bg-zinc-900 gap-2 h-8 text-xs">
            Copiar snippet Phaser
          </Button>
        </div>
      </div>

      {/* ── Waveform ── */}
      {playing && <WaveformBar analyser={analyserRef.current} />}

      {/* ── Step sequencer ── */}
      <div className="border border-zinc-800 bg-zinc-900/50 overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr>
              {/* track header col */}
              <th className="text-left px-3 py-2 text-[10px] tracking-[0.3em] text-zinc-500 uppercase w-28">TRACK</th>
              {/* volume col */}
              <th className="px-2 py-2 text-[10px] tracking-[0.2em] text-zinc-600 w-24">VOL</th>
              {/* step cols */}
              {Array.from({length:STEPS},(_,i) => (
                <th key={i} className={"px-1 py-2 text-center text-[10px] "+(i%4===0?"text-cyan-400":"text-zinc-600")}>
                  {String(i+1).padStart(2,"0")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SYNTH_TRACKS.map(tr => (
              <tr key={tr.id} className="border-t border-zinc-800">
                {/* Label + mute */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleMute(tr.id)}>
                      {muted[tr.id]
                        ? <SpeakerSlash size={12} style={{color:"#52525b"}}/>
                        : <SpeakerHigh  size={12} style={{color:tr.color}}/>
                      }
                    </button>
                    <span className="text-[10px] tracking-[0.25em] uppercase" style={{color: muted[tr.id]?"#52525b":tr.color}}>
                      {tr.label}
                    </span>
                  </div>
                </td>
                {/* Volume slider */}
                <td className="px-2 py-2">
                  <input
                    type="range" min={0} max={1} step={0.05}
                    value={volumes[tr.id]}
                    onChange={e => setVolume(tr.id, e.target.value)}
                    className="w-20 accent-cyan-400"
                    disabled={muted[tr.id]}
                  />
                </td>
                {/* Steps */}
                {pattern[tr.id].map((cell, i) => {
                  const isActive = activeStep === i;
                  return (
                    <td key={i} className={"p-0.5 align-top "+(isActive?"bg-zinc-800/60":"")}>
                      <button
                        onClick={() => toggleCell(tr.id, i)}
                        className={"w-8 h-7 transition-colors border "+(cell.on?"border-transparent":"border-zinc-800 hover:border-zinc-700")}
                        style={{background: cell.on ? (muted[tr.id]?"#3f3f46":tr.color) : "transparent"}}
                      />
                      {tr.id !== "drum" && cell.on && (
                        <select
                          value={cell.note}
                          onChange={e => setNote(tr.id, i, e.target.value)}
                          className="bg-zinc-950 border border-zinc-800 text-[9px] font-mono mt-0.5 w-8 px-0 py-0 text-center outline-none"
                        >
                          {NOTES.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Save row ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-none text-xs font-mono w-56 px-3 py-2 text-zinc-100 outline-none"
          placeholder="Soundtrack name"
        />
        <Button onClick={handleSave} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2">
          <FloppyDisk size={14}/> Guardar track
        </Button>
      </div>

      {/* ── Sample library ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 flex items-center gap-2">
            <Waveform size={13}/> SAMPLES — WAV / MP3 / OGG
          </div>
          <label className="cursor-pointer">
            <input
              type="file" multiple accept="audio/*"
              className="hidden"
              onChange={handleImport}
            />
            <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 border border-zinc-700 hover:bg-zinc-900 px-3 py-1.5 cursor-pointer transition-colors">
              <UploadSimple size={13}/> Importar audio
            </div>
          </label>
          <button
            className="flex items-center gap-2 text-xs font-mono text-cyan-400 border border-zinc-700 hover:bg-zinc-900 px-3 py-1.5"
            onClick={async () => {
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const rec = new MediaRecorder(stream);
                const chunks = [];
                rec.ondataavailable = (e) => chunks.push(e.data);
                rec.onstop = async () => {
                  stream.getTracks().forEach((t) => t.stop());
                  const blob = new Blob(chunks, { type: "audio/webm" });
                  const file = new File([blob], `rec-${Date.now()}.webm`, { type: blob.type });
                  handleImport({ target: { files: [file], value: "" } });
                };
                rec.start();
                toast.success("Grabando 3s…");
                setTimeout(() => rec.stop(), 3000);
              } catch {
                toast.error("No se pudo acceder al micrófono");
              }
            }}
          >
            Grabar 3s
          </button>
        </div>

        <div className="flex gap-1 mb-3">
          {["ALL","SFX","BGM","AMBIENCE","CUSTOM"].map((c) => (
            <button key={c} onClick={() => setSampleCat(c)} className={"px-2 py-1 text-[10px] tracking-widest border "+(sampleCat===c?"border-cyan-400 text-cyan-300":"border-zinc-800 text-zinc-500")}>{c}</button>
          ))}
        </div>
        {samples.filter(s => sampleCat==="ALL" || s.category===sampleCat).length === 0 ? (
          <div className="border border-zinc-800 border-dashed p-8 text-center text-zinc-600 text-xs font-mono">
            Importa archivos WAV, MP3 u OGG. Se usan en tus escenas de Phaser/Godot/Unity.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {samples.filter(s => sampleCat==="ALL" || s.category===sampleCat).map(clip => (
              <SampleClip
                key={clip.id}
                clip={clip}
                onDelete={handleDeleteSample}
                onPlay={(buf, cb) => handlePlaySample(buf || buffersRef.current[clip.id], cb)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Saved tracks ── */}
      {(project.soundtracks||[]).length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">// TRACKS GUARDADOS</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {(project.soundtracks||[]).map(s => (
              <div key={s.id} className="bg-zinc-950 p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm text-zinc-100">{s.name}</div>
                  <button onClick={() => handleDelete(s.id)} className="text-zinc-600 hover:text-red-400">
                    <Trash size={12}/>
                  </button>
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  {s.bpm} BPM · 4 TRACKS · {STEPS} STEPS
                </div>
                <button
                  onClick={() => handleLoad(s)}
                  className="text-[11px] tracking-[0.25em] uppercase text-cyan-400 hover:text-cyan-300 self-start mt-1"
                >
                  CARGAR →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}